import { useEffect, useState } from "react";

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
}

const channels = [
  { icon: "ri-weibo-line", label: "微博" },
  { icon: "ri-wechat-line", label: "微信" },
  { icon: "ri-twitter-x-line", label: "X" },
];

export default function ShareModal({ open, onClose, title }: ShareModalProps) {
  const [copied, setCopied] = useState(false);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    if (!open) return;
    setCopied(false);
    setNotice("");
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setNotice("链接已复制到剪贴板");
    } catch {
      setNotice("当前浏览器限制了自动复制，请手动选择链接复制");
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-background-950/80 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-md animate-fade-up rounded-t-lg border border-background-800 bg-background-900 p-5 sm:rounded-lg sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="font-label text-xs uppercase tracking-[0.22em] text-accent-500">
              Share
            </p>
            <h2 className="mt-2 truncate text-lg font-medium text-foreground-950">
              分享《{title}》
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-background-700 text-foreground-500 transition-colors hover:text-foreground-900"
            aria-label="关闭分享弹窗"
          >
            <i className="ri-close-line" aria-hidden="true"></i>
          </button>
        </div>

        <div className="mt-5 flex items-center gap-2 rounded-md border border-background-700 bg-background-950 px-3 py-2">
          <i className="ri-link text-sm text-foreground-500" aria-hidden="true"></i>
          <input
            readOnly
            value={shareUrl}
            onFocus={(event) => event.currentTarget.select()}
            className="h-8 w-full bg-transparent text-xs text-foreground-700 focus:outline-none"
            aria-label="分享链接"
          />
          <button
            type="button"
            onClick={copyLink}
            className="shrink-0 rounded bg-accent-500 px-3 py-1.5 text-xs text-background-950 transition-colors hover:bg-accent-400"
          >
            {copied ? "已复制" : "复制"}
          </button>
        </div>

        <div className="mt-5">
          <p className="text-xs text-foreground-500">分享到</p>
          <div className="mt-3 flex gap-3">
            {channels.map((channel) => (
              <button
                key={channel.label}
                type="button"
                onClick={() => setNotice(`${channel.label}分享为演示功能，暂未接入外部平台`)}
                className="flex flex-1 flex-col items-center gap-2 rounded-md border border-background-700 py-3 text-xs text-foreground-600 transition-colors hover:border-accent-500 hover:text-accent-400"
              >
                <i className={`${channel.icon} text-lg`} aria-hidden="true"></i>
                {channel.label}
              </button>
            ))}
          </div>
        </div>

        <p className="mt-4 min-h-[18px] text-xs text-foreground-500" aria-live="polite">
          {notice}
        </p>
      </div>
    </div>
  );
}